import { forwardRef, memo, type InputHTMLAttributes } from 'react';
import { cn } from '../../utils/cn';
import { getAccessibleFormProps, getFocusClasses } from '../../utils/accessibility';
import type { BaseComponentProps } from '../../types';
import type { AccessibleFormProps } from '../../types/accessibility.types';

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>,
    BaseComponentProps {
  /**
   * Label del campo
   */
  label?: string;
  /**
   * Stato di errore
   */
  error?: boolean;
  /**
   * Messaggio di errore
   */
  errorMessage?: string;
  /**
   * Testo di aiuto
   */
  helpText?: string;
  /**
   * Stato di successo
   */
  success?: boolean;
  /**
   * Icona da mostrare (sinistra)
   */
  icon?: React.ReactNode;
  /**
   * Icona da mostrare (destra)
   */
  iconRight?: React.ReactNode;
  /**
   * Dimensione
   */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Componente Input
 * 
 * Campo input completamente accessibile con label, error states, help text e icone.
 * Supporta tutte le props native HTML input.
 * Ottimizzato con React.memo per prevenire re-render non necessari.
 * 
 * @example
 * ```tsx
 * <Input label="Email" type="email" placeholder="you@example.com" />
 * <Input label="Password" type="password" error errorMessage="Password troppo corta" />
 * <Input label="Nome" helpText="Inserisci il tuo nome completo" />
 * <Input icon={<SearchIcon />} placeholder="Cerca..." />
 * ```
 */
const InputComponent = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      error,
      success,
      errorMessage,
      helpText,
      icon,
      iconRight,
      size = 'md',
      required,
      ...props
    },
    ref
  ) => {
    const { inputProps, labelProps, errorProps, helpProps } = getAccessibleFormProps({
      id: props.id,
      name: props.name,
      required,
      error,
      errorMessage,
      helpText,
      'aria-label': props['aria-label'],
    });

    const sizeClasses = {
      sm: 'text-sm px-3 py-1.5',
      md: 'text-base px-4 py-2',
      lg: 'text-lg px-5 py-3',
    };

    const inputClasses = cn(
      'w-full rounded-md border transition-colors',
      'bg-base-100 text-base-content placeholder:text-base-content/40',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      sizeClasses[size],
      error && 'border-error focus:border-error focus:ring-error',
      success && 'border-success focus:border-success focus:ring-success',
      !error && !success && 'border-base-300 focus:border-primary focus:ring-primary',
      getFocusClasses(),
      icon && 'pl-10',
      iconRight && 'pr-10',
      className
    );

    return (
      <div className="w-full">
        {label && (
          <label
            {...labelProps}
            className="block text-sm font-medium text-base-content mb-1"
          >
            {label}
            {required && <span className="text-error ml-1" aria-label="richiesto">*</span>}
          </label>
        )}
        
        <div className="relative">
          {icon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40">
              {icon}
            </div>
          )}
          
          <input
            ref={ref}
            {...inputProps}
            {...props}
            className={inputClasses}
          />
          
          {iconRight && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/40">
              {iconRight}
            </div>
          )}
        </div>
        
        {error && errorMessage && (
          <p
            {...errorProps}
            className="mt-1 text-sm text-error"
          >
            {errorMessage}
          </p>
        )}
        
        {!error && helpText && (
          <p
            {...helpProps}
            className="mt-1 text-sm text-base-content/60"
          >
            {helpText}
          </p>
        )}
      </div>
    );
  }
);

InputComponent.displayName = 'Input';

export const Input = memo(InputComponent);

